import { useState, useRef, useEffect, useCallback } from "react";
import { Check } from "lucide-react";
import Image from "../../Home/components/Image";
import { DARK_CARD_VARS } from "./RentAcademyBlock";

const BRAND_GREEN = "#33F7BE";
// RentShield brand (v0 components/rentshield/brand.tsx).
const NAVY = "#0E1A38";
const CORAL = "#F52C52";

/* ------------------------------------------------------------------ */
/* Pricing pennant (v0 components/rentshield/pricing-pennant.tsx)      */
/* ------------------------------------------------------------------ */

// Pennant silhouette with square top corners (flush against the card's top edge) tapering to a
// rounded point, drawn inside a 150 x 158 box. The stroke skips the top edge.
const PENNANT_PATH = "M3,0 H147 V110 Q147,116 142,120 L83,149 Q75,153 67,149 L8,120 Q3,116 3,110 V0 Z";
const PENNANT_STROKE_PATH = "M3,0 V110 Q3,116 8,120 L67,149 Q75,153 83,149 L142,120 Q147,116 147,110 V0";

function PricingPennant({ label, price, unit, caption = "starting at", priceColor = CORAL }) {
  return (
    <div className="relative" style={{ width: 150, height: 158, filter: "drop-shadow(0 14px 24px rgba(14,26,56,0.30))" }}>
      <div
        className="absolute inset-0 bg-white/55 [backdrop-filter:blur(28px)_saturate(160%)]"
        style={{ clipPath: `path('${PENNANT_PATH}')` }}
        aria-hidden="true"
      />
      <svg className="absolute inset-0 h-full w-full" viewBox="0 0 150 158" fill="none" aria-hidden="true">
        <path d={PENNANT_STROKE_PATH} stroke="rgba(255,255,255,0.9)" strokeWidth={4} strokeLinecap="round" />
      </svg>
      <div className="relative flex h-full flex-col items-center px-5 pb-10 pt-5 text-center">
        <span className="shimmer-text text-[10px] font-semibold uppercase tracking-[0.12em]" style={{ color: NAVY }}>
          {label}
        </span>
        <span className="mt-2 h-1 w-8 shrink-0 rounded-full" style={{ backgroundColor: CORAL }} aria-hidden="true" />
        <span className="mt-2 text-[10px] font-medium uppercase tracking-wide" style={{ color: `${NAVY}99` }}>
          {caption}
        </span>
        <span className="shimmer-text mt-2 text-2xl font-bold leading-none" style={{ color: priceColor }}>
          {price}
        </span>
        <span className="mt-1.5 text-[11px] font-medium" style={{ color: `${NAVY}CC` }}>
          {unit}
        </span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Coverage toggle (v0 components/turo-alternate/rentshield-coverage-toggle.tsx) */
/* ------------------------------------------------------------------ */

const IMG = "/img/turo-alternative";

const CONTENT = {
  off: {
    label: "Off Rent",
    when: "Marketplace & Private Rentals",
    heading: "When your vehicle isn't being rented.",
    body: "Billed monthly, Off-Rent insurance keeps your vehicle and business state compliant while insuring it between rentals. Parked, stored, being cleaned, maintained, or otherwise in your possession.",
    points: [
      "Liability, Comprehensive & Collision, Physical damage",
      "Billed monthly, keeps your vehicle & business state compliant",
      "Covers your vehicle, you, and approved staff",
      "Protection during prep, cleaning & downtime",
    ],
    image: { src: `${IMG}/rentshield/off-rent-fleet-team.webp`, alt: "A rental business owner and staff standing in front of their vehicle fleet", position: "center 40%" },
    price: { price: "$40", unit: "/vehicle/mo" },
  },
  on: {
    label: "On Rent",
    when: "During Direct or Private Rental",
    heading: "When your vehicle is being rented.",
    body: "On-Rent insurance provides primary, A-rated commercial coverage during an active rental directly through your own rental business.",
    points: [
      "Liability, Comprehensive & Collision, Physical damage",
      "Driver will not need any further coverage",
      "Protects your business, your vehicle, and the driver",
      "Works for leisure, direct, private, rideshare & gig rentals",
    ],
    image: { src: `${IMG}/rentshield/on-rent-airport.webp`, alt: "Two travelers getting into their rental car in an airport parking lot", position: "75% center" },
    price: { price: "$16", unit: "/vehicle/day" },
  },
  gig: {
    label: "Gig Rental",
    when: "Rideshare & Gig Economy",
    heading: "Insurance for rideshare and gig drivers.",
    body: "Short term and long term gig economy rental insurance for rideshare, delivery, and more. A-rated primary liability, comprehensive, and collision coverage, billed per day to keep your vehicles compliant.",
    points: [
      "Compliant primary coverage for every gig platform",
      "Driver insurance card for Uber, Lyft, DoorDash & more",
      "Billed per day for short & long-term gig rentals",
      "Keeps drivers earning without gaps in coverage",
    ],
    image: { src: `${IMG}/rentshield/rideshare-hero-pickup.webp`, alt: "Rideshare driver holding the car door open for a passenger on a city street" },
    price: { price: "$16", unit: "/vehicle/day" },
  },
  roadside: {
    label: "Roadside",
    when: "24/7 Roadside Support",
    heading: "Help on the road, built in.",
    body: "Give every renter 24/7 roadside support with smart dispatch and real-time communication, so a breakdown never becomes your problem.",
    points: [
      "Tow, jump-start, lockout, tire change & fuel delivery",
      "24/7 nationwide dispatch with live truck tracking",
      "Follows the driver with no upfront cost at the roadside",
      "Keeps renters moving so breakdowns never reach you",
    ],
    image: { src: `${IMG}/inshur/roadside-assistance.webp`, alt: "A roadside assistance technician helping a driver next to an SUV parked on the shoulder at golden hour", position: "78% center" },
    price: { price: "$10", unit: "/mth" },
  },
  warranty: {
    label: "Warranty",
    when: "Mechanical Protection",
    heading: "Coverage for the repairs that stop revenue.",
    body: "Mechanical breakdown protection that keeps your rental vehicles on the road instead of sitting in the shop with unexpected repair bills.",
    points: [
      "Engine, transmission & drivetrain coverage",
      "Predictable, fleet-friendly fixed pricing",
      "Fast approved-shop claims keep vehicles earning",
      "Nationwide network of approved repair shops",
    ],
    image: { src: `${IMG}/inshur/vehicle-warranty.webp`, alt: "An automotive mechanic inspecting the engine bay of an SUV with a diagnostic tablet in a modern service bay" },
    price: { price: "$49", unit: "/mth" },
  },
};

const TABS = ["off", "on", "gig", "roadside", "warranty"];
// Products not powered by INSHUR show the plain RentShield logo instead of the INSHUR lockup.
const NOT_INSHUR_TABS = ["roadside", "warranty"];

function RentShieldCoverageToggle({ offRentPrice, glassPills, darkBadgeText, logo, plainLogo, plainLogoClassName, logoClassName = "h-16 sm:h-20" }) {
  const [tab, setTab] = useState("off");
  const [hovered, setHovered] = useState(null);
  const usePlainLogo = !!plainLogo && NOT_INSHUR_TABS.includes(tab);
  const base = CONTENT[tab];
  const active = tab === "off" && offRentPrice && base.price ? { ...base, price: { ...base.price, price: offRentPrice } } : base;

  const listRef = useRef(null);
  const btnRefs = useRef({});
  const [indicator, setIndicator] = useState(null);

  // The green "liquid" indicator rests on the selected pill and slides to the newly selected one.
  const measure = useCallback(() => {
    const btn = btnRefs.current[tab];
    const list = listRef.current;
    if (!btn || !list) return;
    setIndicator({ left: btn.offsetLeft, width: btn.offsetWidth });
  }, [tab]);

  useEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(list);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  return (
    <div>
      {logo && (
        <div className="mb-20">
          <img
            src={usePlainLogo ? plainLogo : logo}
            alt={usePlainLogo ? "RentShield logo" : "RentShield powered by INSHUR logo"}
            className={`${usePlainLogo ? plainLogoClassName ?? logoClassName : logoClassName} w-auto`}
          />
        </div>
      )}
      {/* Toggle — glass pills with a green "liquid" indicator */}
      <div
        ref={listRef}
        role="tablist"
        aria-label="Coverage options"
        onMouseLeave={() => setHovered(null)}
        className="relative mt-6 flex w-full max-w-full items-center gap-1.5 overflow-x-auto p-1 sm:mt-10 sm:w-fit"
      >
        {indicator && (
          <span
            aria-hidden="true"
            className="absolute left-0 top-1 z-0 rounded-full"
            style={{
              height: "calc(100% - 0.5rem)",
              width: indicator.width,
              transform: `translateX(${indicator.left}px)`,
              backgroundColor: BRAND_GREEN,
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 2px rgba(0,0,0,0.14)",
              transition: "transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), width 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
            }}
          />
        )}
        {TABS.map((key) => {
          const isSelected = key === tab;
          const isHovered = key === hovered;
          return (
            <button
              key={key}
              ref={(el) => {
                btnRefs.current[key] = el;
              }}
              type="button"
              role="tab"
              aria-selected={isSelected}
              onClick={() => setTab(key)}
              onMouseEnter={() => setHovered(key)}
              onFocus={() => setHovered(key)}
              className={`relative z-10 shrink-0 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-[13px] font-semibold transition-colors duration-300 ${
                isSelected
                  ? "border-transparent bg-transparent shadow-none"
                  : glassPills
                  ? `border-white/20 shadow-[inset_0_1px_0_rgba(255,255,255,0.25)] [backdrop-filter:blur(20px)] ${isHovered ? "bg-white/15" : "bg-white/[0.06]"}`
                  : `border-white/25 shadow-[inset_0_1px_0_rgba(255,255,255,0.4)] [backdrop-filter:blur(20px)_saturate(180%)] ${isHovered ? "bg-white/20" : "bg-white/10"}`
              }`}
              style={{ color: isSelected ? "#000000" : glassPills ? "rgba(255,255,255,0.85)" : "rgb(var(--tw-c-muted-foreground))" }}
            >
              {CONTENT[key].label}
            </button>
          );
        })}
      </div>

      {/* Panel: text left, squared image aside right. Keyed on the tab so it re-animates in. */}
      <div key={tab} className="tnt-panel-in mt-6 lg:flex lg:items-stretch lg:gap-10">
        <div className="min-w-0 lg:flex lg:flex-1 lg:flex-col">
          <div className="flex items-center gap-2">
            <span className="text-xs font-normal uppercase tracking-[0.16em]" style={{ color: BRAND_GREEN }}>
              {active.label}
            </span>
            <span className="text-sm font-medium text-foreground">&middot; {active.when}</span>
          </div>
          <h4 className="mt-2 font-heading text-2xl sm:text-[28px] font-normal tracking-tight text-balance text-foreground">
            {active.heading}
          </h4>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-pretty text-muted-foreground">{active.body}</p>
          <ul className="mt-6 flex flex-col gap-4 lg:flex-1 lg:justify-between">
            {active.points.map((p) => (
              <li key={p} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: BRAND_GREEN }}>
                  <Check className="h-3 w-3 text-black" strokeWidth={3} aria-hidden="true" />
                </span>
                <span className="text-base leading-relaxed text-foreground">{p}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Right — squared image aside (pulled up so it stays put as pills/copy move down) */}
        <div className="mt-10 shrink-0 flex items-start justify-center lg:justify-end lg:-mt-4">
          <div className="tnt-dark-card relative aspect-square w-full max-w-sm lg:w-[380px] overflow-hidden rounded-3xl bg-black shadow-xl">
            <Image
              src={active.image.src}
              alt={active.image.alt}
              fill
              className="object-cover"
              style={{ objectPosition: active.image.position ?? "center" }}
            />
            {active.price && (
              <div className="absolute bottom-4 left-4 z-10 rounded-2xl border border-white/40 bg-white/20 px-4 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.5)] [backdrop-filter:blur(16px)_saturate(160%)]">
                <span className={`block text-[10px] font-medium uppercase tracking-wide ${darkBadgeText ? "text-black/70" : "text-white/80"}`}>
                  Starting at
                </span>
                <span className={`text-lg font-bold ${darkBadgeText ? "text-black" : "text-white"}`}>{active.price.price}</span>
                <span className={`ml-1 text-xs font-medium ${darkBadgeText ? "text-black/70" : "text-white/80"}`}>{active.price.unit}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* RentShield card (v0 TuroAltInsurance → InsuranceCard, gradient + plainLogo branch) */
/* ------------------------------------------------------------------ */

export function RentShieldInsurance({
  logo,
  gradient,
  offRentPrice = "$40",
  glassPills,
  darkBadgeText,
  logoClassName = "h-16 sm:h-20",
  plainLogo,
  plainLogoClassName,
}) {
  return (
    <section id="private-rental-insurance" className="bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24">
        <div className="mt-10">
          <div
            className="tnt-dark-card relative overflow-hidden rounded-3xl p-6 sm:p-10 lg:p-14 shadow-lg"
            style={{ ...DARK_CARD_VARS, backgroundColor: "#050505", backgroundImage: gradient }}
          >
            {/* Pricing pennants — top right corner */}
            <div className="pointer-events-none absolute right-6 -top-px z-20 hidden items-start gap-3 sm:flex">
              <PricingPennant label="Off rent" price={offRentPrice} unit="/vehicle/mo" />
              <PricingPennant label="On rent" price="$16" unit="/vehicle/day" />
            </div>

            <RentShieldCoverageToggle
              logo={logo}
              plainLogo={plainLogo}
              logoClassName={logoClassName}
              plainLogoClassName={plainLogoClassName}
              offRentPrice={offRentPrice}
              glassPills={glassPills}
              darkBadgeText={darkBadgeText}
            />
            {/* v0 keeps its (empty) link row here when the footer is hidden; the margin is kept so the
                card height matches. */}
            <div className="mt-8" />
          </div>
        </div>
      </div>
    </section>
  );
}
